import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import moment from 'moment';
import { ModalShell } from '../lib/ui';
import { findLostCustomers } from '../lib/lostCustomers';

const API = 'https://teardrophydro.co.uk/garageFarm';
// The orders route takes a look-back window; this is "everything".
const ALL_HISTORY_DAYS = 3650;

const KINDS = {
  'one-off':   { label: 'Bought once',        hint: 'One visit, never came back',      bg: '#fef3c7', fg: '#92400e' },
  stopped:     { label: 'Stopped reordering', hint: 'Regulars who have gone quiet',    bg: '#fee2e2', fg: '#b91c1c' },
  cancelled:   { label: 'Cancelled sub',      hint: "Cancelled and haven't bought since", bg: '#f3e8ff', fg: '#7c3aed' },
};

const THRESHOLDS = [30, 45, 60, 90];
const SORTS = [['value', 'Most spent'], ['recent', 'Most recent'], ['orders', 'Most orders']];

const muted = { fontSize: 12, color: 'var(--muted)' };
const controlLabel = { fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em' };
const cell = { verticalAlign: 'top', padding: '12px 10px', fontSize: 13, lineHeight: 1.5 };

const tag = (kind) => ({
  display: 'inline-block', marginTop: 4,
  background: KINDS[kind].bg, color: KINDS[kind].fg, fontSize: 10, fontWeight: 700,
  padding: '1px 6px', borderRadius: 'var(--r-sm)', letterSpacing: '0.04em',
  textTransform: 'uppercase', whiteSpace: 'nowrap',
});

function SummaryTile({ active, onClick, label, hint, count, spent, colour }) {
  return (
    <button
      onClick={onClick}
      style={{
        flex: '1 1 180px', textAlign: 'left', cursor: 'pointer', margin: 0,
        padding: '10px 14px', borderRadius: 'var(--r-lg)',
        border: `1px solid ${active ? colour : 'var(--border)'}`,
        borderTop: `3px solid ${colour}`,
        background: active ? 'var(--panel-2)' : 'var(--panel)',
        color: 'var(--text)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span style={{ fontSize: 22, fontWeight: 800, color: colour }}>{count}</span>
        <span style={{ fontSize: 13, fontWeight: 700 }}>{label}</span>
      </div>
      <div style={muted}>{hint} · £{spent.toFixed(2)}</div>
    </button>
  );
}

function Address({ address }) {
  if (!address) return <span style={muted}>No address on file</span>;
  const { line1, line2, city, postcode } = address;
  return (
    <>
      <div>{line1}</div>
      {line2 && <div>{line2}</div>}
      <div>{[city, postcode].filter(Boolean).join(', ')}</div>
    </>
  );
}

export default function LostCustomersModal({ onClose }) {
  const [orders, setOrders]   = useState(null);
  const [subs, setSubs]       = useState([]);
  const [error, setError]     = useState('');
  const [minDays, setMinDays] = useState(45);
  const [kind, setKind]       = useState('all');
  const [sortBy, setSortBy]   = useState('value');

  useEffect(() => {
    const headers = { Authorization: `Bearer ${localStorage.getItem('accessToken')}` };
    Promise.all([
      axios.get(`${API}/orders?days=${ALL_HISTORY_DAYS}`, { headers }),
      // Subscriptions only refine the list (hide active subscribers, add
      // cancelled ones), so a failure here shouldn't block the view.
      axios.get(`${API}/subscriptions`, { headers }).catch(() => ({ data: [] })),
    ])
      .then(([o, s]) => {
        setOrders(o.data.recent_orders || []);
        setSubs(Array.isArray(s.data) ? s.data : []);
      })
      .catch((err) => {
        console.error('Lost customers fetch failed', err);
        setError('Could not load order history.');
      });
  }, []);

  const lost = useMemo(
    () => (orders ? findLostCustomers(orders, subs, { minDaysSince: minDays }) : []),
    [orders, subs, minDays]
  );

  const groups = useMemo(() => {
    const acc = { all: { count: 0, spent: 0 } };
    Object.keys(KINDS).forEach((k) => { acc[k] = { count: 0, spent: 0 }; });
    lost.forEach((c) => {
      [acc.all, acc[c.kind]].forEach((g) => { g.count += 1; g.spent += c.totalSpent; });
    });
    return acc;
  }, [lost]);

  const visible = useMemo(() => {
    const rows = kind === 'all' ? [...lost] : lost.filter((c) => c.kind === kind);
    if (sortBy === 'recent') rows.sort((a, b) => a.daysSince - b.daysSince);
    if (sortBy === 'orders') rows.sort((a, b) => b.orderCount - a.orderCount || b.totalSpent - a.totalSpent);
    return rows;
  }, [lost, kind, sortBy]);

  return (
    <ModalShell onClose={onClose} width={1200} maxWidth="96vw" ariaLabel="Lost customers" panelStyle={{ padding: '24px 28px 20px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 18 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Lost customers</h2>
          <p style={{ ...muted, margin: '3px 0 0' }}>
            People who bought and haven't come back. Regulars only appear once they've been quiet for
            twice their usual gap between orders. Active subscribers are left out.
          </p>
        </div>
        <button onClick={onClose} aria-label="Close" style={{
          background: 'var(--panel-2)', border: 'none', borderRadius: 'var(--r-md)',
          width: 30, height: 30, cursor: 'pointer', fontSize: 16, color: 'var(--muted)', flexShrink: 0,
        }}>✕</button>
      </div>

      {error && <p style={{ color: '#dc2626', fontSize: 13 }}>{error}</p>}
      {!orders && !error && <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)' }}>Loading order history…</div>}

      {orders && (
        <>
          {/* Summary tiles double as the type filter */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
            <SummaryTile
              active={kind === 'all'} onClick={() => setKind('all')}
              label="All lost" hint="Everyone below" colour="var(--brand)"
              count={groups.all.count} spent={groups.all.spent}
            />
            {Object.entries(KINDS).map(([k, meta]) => (
              <SummaryTile
                key={k} active={kind === k} onClick={() => setKind(k)}
                label={meta.label} hint={meta.hint} colour={meta.fg}
                count={groups[k].count} spent={groups[k].spent}
              />
            ))}
          </div>

          {/* Toolbar */}
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap',
            padding: '10px 0', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)', marginBottom: 4,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={controlLabel}>Quiet for at least</span>
              <div className="button-group">
                {THRESHOLDS.map((d) => (
                  <button key={d} className={minDays === d ? 'active' : ''} onClick={() => setMinDays(d)}>{d} days</button>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={controlLabel}>Sort</span>
              <div className="button-group">
                {SORTS.map(([v, l]) => (
                  <button key={v} className={sortBy === v ? 'active' : ''} onClick={() => setSortBy(v)}>{l}</button>
                ))}
              </div>
            </div>
          </div>

          {visible.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)' }}>Nobody in this group - nice.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="stats-table" style={{ width: '100%', minWidth: 940 }}>
                <thead>
                  <tr>
                    <th style={{ width: '17%' }}>Customer</th>
                    <th style={{ width: '19%' }}>Contact</th>
                    <th style={{ width: '19%' }}>Address</th>
                    <th style={{ width: '13%' }}>History</th>
                    <th style={{ width: '11%' }}>Last bought</th>
                    <th>Usually buys</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((c) => (
                    <tr key={c.key}>
                      <td style={cell}>
                        <div style={{ fontWeight: 700 }}>{c.name || '-'}</div>
                        <span style={tag(c.kind)}>{KINDS[c.kind].label}</span>
                      </td>
                      <td style={{ ...cell, wordBreak: 'break-word' }}>
                        {c.email && <div>{c.email}</div>}
                        {c.phone && (
                          <a href={`tel:${c.phone}`} style={{ color: 'var(--brand-2)', textDecoration: 'none', fontWeight: 600 }}>
                            {c.phone}
                          </a>
                        )}
                        {!c.email && !c.phone && <span style={muted}>-</span>}
                      </td>
                      <td style={cell}><Address address={c.address} /></td>
                      <td style={cell}>
                        <div style={{ fontWeight: 700 }}>£{c.totalSpent.toFixed(2)}</div>
                        <div style={muted}>
                          {c.orderCount} order{c.orderCount !== 1 ? 's' : ''}
                          {c.avgGapDays != null && <> · every ~{c.avgGapDays}d</>}
                        </div>
                      </td>
                      <td style={{ ...cell, whiteSpace: 'nowrap' }}>
                        <div>{moment(c.lastOrder).format('D MMM YYYY')}</div>
                        <div style={muted}>{c.daysSince} days ago</div>
                      </td>
                      <td style={cell}>
                        {c.favourites.length
                          ? c.favourites.map((f) => <div key={f}>{f}</div>)
                          : <span style={muted}>-</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </ModalShell>
  );
}
